import { useId } from 'react';
import styled, { keyframes } from 'styled-components';
import { unthemed } from 'tokens';

// Tune the phase-right feel here.
//
// `ease-in-out` is the canonical ease but the geometry fights it: the dramatic
// phase change happens in the middle of the offset range (when the shadow is
// crossing the lit circle), and ease-in-out speeds through the middle and
// dwells on the flat full-moon endpoints. The 'linger-*' presets invert that —
// fast through the visually quiet ends, slow through the moon-phase moment.
type PhaseEase =
	| 'linear'
	| 'ease-in-out'
	| 'smooth'
	| 'linger-soft'
	| 'linger'
	| 'linger-strong'
	| 'linger-extreme';

const PHASE_EASE: PhaseEase = 'ease-in-out';
const PHASE_DURATION_SECONDS = 2.4;

const EASE: Record<PhaseEase, string> = {
	linear: 'linear',
	'ease-in-out': 'ease-in-out',
	smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
	'linger-soft': 'cubic-bezier(0.4, 0.6, 0.6, 0.4)',
	linger: 'cubic-bezier(0.25, 0.85, 0.75, 0.15)',
	'linger-strong': 'cubic-bezier(0.1, 0.9, 0.9, 0.1)',
	'linger-extreme': 'cubic-bezier(0, 1, 1, 0)',
};

const COLOR = unthemed.gray[400];

interface LoaderProps {
	// loader is square — one dimension
	size?: number;
}

// Shadow enters from the left edge and exits the right. Both endpoints are
// tangent off-frame, so the wrap is seamless (full moon at both ends).
const phaseRight = keyframes`
	from { transform: translate(-84px, 0); }
	to   { transform: translate(84px, 0); }
`;

const PhaseShadowGroup = styled.g`
	animation: ${phaseRight} ${PHASE_DURATION_SECONDS}s ${EASE[PHASE_EASE]} infinite;
`;

const Wrap = styled.div<{ $size: number }>`
	width: ${(p) => p.$size}px;
	height: ${(p) => p.$size}px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
`;

export const Loader = ({ size = 32 }: LoaderProps) => {
	const maskId = `loader-${useId()}`;
	return (
		<Wrap $size={size}>
			<svg width={size} height={size} viewBox="0 0 100 100">
				<defs>
					<mask id={maskId}>
						<rect width="100" height="100" fill="black" />
						<circle cx="50" cy="50" r="42" fill="white" />
						<PhaseShadowGroup>
							<circle cx="50" cy="50" r="42" fill="black" />
						</PhaseShadowGroup>
					</mask>
				</defs>
				<circle cx="50" cy="50" r="42" fill={COLOR} mask={`url(#${maskId})`} />
			</svg>
		</Wrap>
	);
};
